import { getTextWidth } from '../common.js';
import distinctColors from '../distinctColors/index.js';

// const distinctColors = require('distinct-colors'); // tslint:disable-line

export const defaultColors = [
  { _rgb: [78, 193, 239, 1] },
  { _rgb: [96, 233, 179, 1] },
  { _rgb: [255, 198, 94, 1] },
  { _rgb: [39, 108, 152, 1] },
  { _rgb: [55, 174, 140, 1] },
  { _rgb: [237, 138, 26, 1] },
  { _rgb: [0, 35, 68, 1] },
];

export const generateRgbColors = (num) => {
  return defaultColors.filter((_, i) => i < num)
    .concat(distinctColors({
      count: Math.max(num - defaultColors.length, 0),
      lightMin: 30,
      lightMax: 70,
    }))
    .map((f) => ({ r: f._rgb[0], g: f._rgb[1], b: f._rgb[2], a: f._rgb[3] }));
};

export const generateColors = (num) => {
  return generateRgbColors(num)
    .map((rgba) => `rgb(${rgba.r},${rgba.g},${rgba.b}, ${rgba.a})`);
};

const syllableRegex = /[^aeiouy]*[aeiouy]+(?:[^aeiouy]*$|[^aeiouy](?=[^aeiouy]))?/gi;
const syllabify = (words) => {
  return words.match(syllableRegex) || [words];
};

const createSentance = (words, limitFnc) => {
  let numOfWords = 0;
  let isFullfiled = false;
  const sentance = words.reduce((acc, f) => {
    const str = acc ? `${acc} ${f.word}` : f.word;
    if (!isFullfiled && limitFnc(str)) {
      numOfWords++;
      return str;
    } else {
      isFullfiled = true;
      return acc;
    }
  }, '');
  return {
    sentance,
    usedWords: words.filter((_, ind) => ind < numOfWords),
    restOfWords: words.filter((_, ind) => ind >= numOfWords),
  };
};

export const dashSentance = (sentance, limitFnc) => {
  let isFullfiled = false;
  const startText = sentance.sentance + ' -';
  const add = syllabify(sentance.restOfWords[0].word)
    .reduce((acc, f) => {
      if (!isFullfiled && limitFnc(startText + acc + f)) {
        return acc + f;
      } else {
        isFullfiled = true;
        return acc;
      }
    }, '');
  if (add.length) {
    sentance.sentance += ' ' + add + '-';
    sentance.usedWords.push({ ...sentance.restOfWords[0], word: add + '-' });
    sentance.restOfWords[0] = ({
      ...sentance.restOfWords[0],
      word: sentance.restOfWords[0].word.substring(add.length),
    });

    sentance.restOfWords = sentance.restOfWords.map((f) => ({
      ...f,
      ...(f.from ? { from: f.from + 1 } : {}),
    }));
    if (sentance.restOfWords[0].highlightedPositions) {
      sentance.restOfWords[0].highlightedPositions = sentance.restOfWords[0].highlightedPositions
        .map((g) => ({ ...g, from: Math.max(g.from - add.length, 0), length: g.from + g.length - add.length }))
        .filter((g) => g.length > 0);
    }
  }
  return sentance;
};

export const splitSentanceToWords = (text) => text.split(' ').filter((f) => f && ['\n', '\r\n', '\r'].indexOf(f) === -1);

export const splitWordsToLines = (words, limitFnc, numOfLines) => {
  const sentances = [];
  let sentance = { sentance: '', restOfWords: words };
  sentance = createSentance(sentance.restOfWords, limitFnc);
  do {
    if (sentance.restOfWords.length) {
      sentance = dashSentance(sentance, limitFnc);
      sentances.push(sentance);
      if (sentances.length >= numOfLines - 1) {
        const str = sentance.restOfWords.map((f) => f.word).join(' ');
        if (str.length) {
          sentances.push({
            sentance: str,
            usedWords: [...sentance.restOfWords],
            restOfWords: [],
          });
        }
        break;
      }
    } else {
      sentances.push(sentance);
      break;
    }
  } while (true); // eslint-disable-line  
  return sentances;
};

export const trimDotEnd = (str, limitFnc) => {
  if (!limitFnc(str.sentance)) {
    return trimSentance(str, limitFnc);
  }
  return str;
};

const middleFnc = (min, max) => {
  let sent = max;
  return (goDown) => {
    if (goDown) {
      max = sent;
    } else {
      min = sent;
    }

    sent = min + Math.floor((max - min) / 2);
    return sent;
  };
};
const trimSentance = (str, limitFnc) => {
  const midFnc = middleFnc(0, str.sentance.length);

  let goDown = true;
  let lastLength = str.sentance.length;
  do {
    var retStr = `${str.sentance.substring(0, midFnc(goDown))}...`;  // tslint:disable-line
    if (lastLength === retStr.length) {
      break;
    }
    lastLength = retStr.length;
    goDown = !limitFnc(retStr);
  } while (true); // eslint-disable-line  

  let numOfUsedChars = retStr.length - 3;
  let trimWord = str.usedWords.reduce((acc, f) =>
    acc.word ? acc : (acc <= numOfUsedChars && (acc + f.word.length) >= numOfUsedChars ? f : (acc + f.word.length + 1)), 0);

  let usedWords = str.usedWords.filter((_, ind) => ind < str.usedWords.indexOf(trimWord));
  let delta = usedWords
    .filter((_, ind) => ind < str.usedWords.indexOf(trimWord))
    .reduce((acc, f) => acc + f.word.length + 1, 0) + trimWord.word.length - numOfUsedChars;
  usedWords.push({ ...trimWord, word: trimWord.word.substring(0, trimWord.word.length - delta) + '...' });

  return {
    sentance: retStr,
    usedWords,
    restOfWords: [
      { ...trimWord, trimmedWord: trimWord.word.substring(0, trimWord.word.length - delta) + '...' },
      ...str.usedWords.filter((_, ind) => ind > str.usedWords.indexOf(trimWord)),
      ...str.restOfWords,
    ],
  };
};

const textWidthFnc = getTextWidth(`11pt Source Sans Pro, sans-serif`);
export const isTextWithinLimit = (limit) => (str) => textWidthFnc(str) / 1.16 <= limit;

export const deg2rad = (deg) => deg * Math.PI / 180;
export const rad2deg = (rad) => rad * 180 / Math.PI;
let earthRadius = 6378137.0;
export const rad2meters = (rad) => earthRadius * rad;
export const meters2rad = (length) => length / earthRadius;

export const polarPoint = (origin, distance, bearingRad) => {
  let longitude1 = deg2rad(origin[0]);
  let latitude1 = deg2rad(origin[1]);

  let latitude2 = Math.asin(Math.sin(latitude1) * Math.cos(distance) + Math.cos(latitude1) * Math.sin(distance) * Math.cos(bearingRad));
  let longitude2 = longitude1 + Math.atan2(Math.sin(bearingRad) * Math.sin(distance) * Math.cos(latitude1), Math.cos(distance) - Math.sin(latitude1) * Math.sin(latitude2));
  let lng = rad2deg(longitude2);
  let lat = rad2deg(latitude2);
  return [lng, lat];
};

export const circlePolyline = (center, radius) => {
  let steps = 64;
  return new Array(steps).fill(0).map((_, ind) => ind).concat(0)
    .map((f) => polarPoint(center, radius, f * 2 * Math.PI / steps));
};

// export const bearing = (coordinate1: any, coordinate2: any) => {
//   var lon1 = deg2rad(coordinate1[0]);
//   var lon2 = deg2rad(coordinate2[0]);
//   var lat1 = deg2rad(coordinate1[1]);
//   var lat2 = deg2rad(coordinate2[1]);
//   var a = Math.sin(lon2 - lon1) * Math.cos(lat2);
//   var b = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
//   return Math.atan2(a, b);
// }

// export const normalizeAngle = function(angle: number) {
//   if (angle < 0) return normalizeAngle(angle + 2 * Math.PI);
//   if (angle >= 2*Math.PI) return normalizeAngle(angle - 2*Math.PI);
//   return angle;
// }

// exports.factors = {
//     centimeters: exports.earthRadius * 100,
//     centimetres: exports.earthRadius * 100,
//     degrees: exports.earthRadius / 111325,
//     feet: exports.earthRadius * 3.28084,
//     inches: exports.earthRadius * 39.370,
//     kilometers: exports.earthRadius / 1000,
//     kilometres: exports.earthRadius / 1000,
//     meters: exports.earthRadius,
//     metres: exports.earthRadius,
//     miles: exports.earthRadius / 1609.344,
//     millimeters: exports.earthRadius * 1000,
//     millimetres: exports.earthRadius * 1000,
//     nauticalmiles: exports.earthRadius / 1852,
//     radians: 1,
//     yards: exports.earthRadius / 1.0936,
// };
